// client/src/App.js
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/auth/AuthContext';
import { Provider as JotaiProvider } from 'jotai';
import './App.css';

// Core components
import LoadingScreen from './components/common/LoadingScreen';
import Notifications from './components/common/Notifications';
import ErrorBoundary from './components/common/ErrorBoundary';
import PrivateRoute from './components/routing/PrivateRoute';
import AIAssistant from './components/AIAssistant';

// Lazy-loaded components
const HomePage = lazy(() => import('./components/HomePage'));
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const DashboardLayout = lazy(() => import('./components/layouts/DashboardLayout'));
const NotFound = lazy(() => import('./components/common/NotFound'));
const MainLayout = lazy(() => import('./components/layouts/MainLayout'));

// Public pages
const AboutPage = lazy(() => import('./components/public/AboutPage'));
const ContactPage = lazy(() => import('./components/public/ContactPage'));
const EventsPage = lazy(() => import('./components/public/EventsPage'));
const LodgesPage = lazy(() => import('./components/public/LodgesPage'));
const EducationPage = lazy(() => import('./components/public/EducationPage'));
const ResourcesPage = lazy(() => import('./components/public/ResourcesPage'));
const GrandLodgeAwards = lazy(() => import('./components/public/resources/GrandLodgeAwards'));
const FormsPage = lazy(() => import('./components/public/resources/FormsPage'));
const ResearchLodgePage = lazy(() => import('./components/public/resources/ResearchLodgePage'));
const CampingClubPage = lazy(() => import('./components/public/resources/CampingClubPage'));
const DigestLawsPage = lazy(() => import('./components/public/resources/DigestLawsPage'));
const MasonicOrganizationsPage = lazy(() => import('./components/public/resources/MasonicOrganizationsPage'));
const DeputySchoolPage = lazy(() => import('./components/public/resources/DeputySchoolPage'));
// Make sure this path matches your folder structure exactly
const IntroductionToCornerstone = lazy(() => import('./components/public/resources/IntroductionToCornerstone'));

// Philanthropy Pages
const PhilanthropyPage = lazy(() => import('./components/public/PhilanthropyPage'));
const EndowmentFundPage = lazy(() => import('./components/public/philanthropy/EndowmentFundPage'));
const LicensePlatesPage = lazy(() => import('./components/public/philanthropy/LicensePlatesPage'));
const BuildersWalkPage = lazy(() => import('./components/public/philanthropy/BuildersWalkPage'));
const ScholarshipPage = lazy(() => import('./components/public/philanthropy/ScholarshipPage'));
const ScottishRitePage = lazy(() => import('./components/public/philanthropy/ScottishRitePage'));
const RoyalArchPage = lazy(() => import('./components/public/philanthropy/RoyalArchPage'));
const CrypticMasonsPage = lazy(() => import('./components/public/philanthropy/CrypticMasonsPage'));
const KnightsTemplarPage = lazy(() => import('./components/public/philanthropy/KnightsTemplarPage'));
const ShrinersPage = lazy(() => import('./components/public/philanthropy/ShrinersPage'));
const MMRIPage = lazy(() => import('./components/public/philanthropy/MMRIPage'));


// Configure Axios default baseURL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '/api';

// Create a theme context
export const ThemeContext = React.createContext({
  darkMode: false,
  toggleDarkMode: () => {}
});

// Create assistant context
export const AlienContext = React.createContext({
  assistantEnabled: true,
  toggleAlien: () => {}
});

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 300000, // 5 minutes
    },
  },
});

// Add animation styles to head
const addAnimationStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.type = 'text/css';
  styleEl.innerHTML = `
    @keyframes slow-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .animate-slow-spin {
      animation: slow-spin 30s linear infinite;
    }
    
    @keyframes pulse-slow {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
    
    .animate-pulse-slow {
      animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    .bg-grid-pattern {
      background-image: radial-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px);
      background-size: 20px 20px;
    }
    
    @keyframes float {
      0% { transform: translateY(0px) translateX(0px); opacity: 0.5; }
      50% { transform: translateY(-10px) translateX(5px); opacity: 0.7; }
      100% { transform: translateY(0px) translateX(0px); opacity: 0.5; }
    }
    
    @keyframes fade-in-up {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .animate-float {
      animation: float 6s ease-in-out infinite;
    }
    
    .animate-fade-in-up {
      animation: fade-in-up 0.5s ease-out forwards;
    }
    
    /* Light beam animation for navbar */
    @keyframes light-beam {
      0% {
        left: -40%;
        opacity: 0;
      }
      20% {
        opacity: 0.05;
      }
      50% {
        opacity: 0.05;
      }
      100% {
        left: 140%;
        opacity: 0;
      }
    }

    .animate-light-beam {
      animation: light-beam 8s infinite linear;
    }

    /* Menu icon animation */
    @keyframes spin-once {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(90deg);
      }
    }

    .animate-spin-once {
      animation: spin-once 0.3s ease-in-out forwards;
    }
    
    /* Masonic compass slow rotation */
    @keyframes compass-rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .animate-compass-slow {
      animation: compass-rotate 30s infinite linear;
    }
    
    /* Modified Tailwind classes */
    .dropdown-shadow {
      box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.2), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    }

    .hover-glow:hover {
      box-shadow: 0 0 12px rgba(59, 130, 246, 0.5);
    }
  `;
  document.head.appendChild(styleEl);
};

// Main App component
const App = () => {
  // Set up theme state
  const [darkMode, setDarkMode] = useState(() => {
    // Check for saved theme preference or use system preference
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || 
      (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Set up assistant state
  const [assistantEnabled, setAssistantEnabled] = useState(() => {
    // Check for saved assistant preference, default to enabled
    const savedPref = localStorage.getItem('assistantEnabled');
    return savedPref === null ? true : savedPref === 'true';
  });

  // Toggle theme function
  const toggleDarkMode = () => {
    setDarkMode(prevMode => !prevMode);
  };

  // Toggle assistant function
  const toggleAlien = () => {
    setAssistantEnabled(prevEnabled => {
      const newValue = !prevEnabled;
      localStorage.setItem('assistantEnabled', newValue.toString());
      return newValue;
    });
  };

  // Apply theme class to document when theme changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);
  
  // Add animations styles when component mounts
  useEffect(() => {
    addAnimationStyles();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <JotaiProvider>
        <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
          <AlienContext.Provider value={{ assistantEnabled, toggleAlien }}>
            <BrowserRouter>
              <AuthProvider>
                <div className="flex flex-col min-h-screen w-full bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
                  <ErrorBoundary fallback={
                    <div className="text-center py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
                      <p className="mb-4 text-gray-700 dark:text-gray-300">We encountered an error while rendering this page.</p>
                      <button 
                        onClick={() => window.location.reload()} 
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Refresh Page
                      </button>
                    </div>
                  }>
                    <Suspense fallback={<LoadingScreen />}>
                      <Routes>
                        {/* Dashboard route - Direct render */}
                        <Route path="/dashboard/*" element={
                          <PrivateRoute>
                            <DashboardLayout />
                          </PrivateRoute>
                        } />
                        
                        {/* All other routes through MainLayout */}
                        <Route element={<MainLayout />}>
                          {/* Public routes */}
                          <Route path="/" element={<HomePage />} />
                          <Route path="/about/*" element={<AboutPage />} />
                          <Route path="/contact" element={<ContactPage />} />
                          <Route path="/events" element={<EventsPage />} />
                          <Route path="/lodges" element={<LodgesPage />} />
                          <Route path="/lodge-locator" element={<LodgesPage />} />
                          <Route path="/education/*" element={<EducationPage />} />
                          <Route path="/resources" element={<ResourcesPage />} />
                          <Route path="/resources/awards" element={<GrandLodgeAwards />} />
                          <Route path="/resources/forms" element={<FormsPage />} />
                          <Route path="/resources/research-lodge" element={<ResearchLodgePage />} />
                          <Route path="/resources/camping-club" element={<CampingClubPage />} />
                          <Route path="/resources/digest-laws" element={<DigestLawsPage />} />
                          <Route path="/resources/masonic-organizations" element={<MasonicOrganizationsPage />} />
                          <Route path="/resources/deputy-schools" element={<DeputySchoolPage />} />
                          <Route path="/resources/cornerstone" element={<IntroductionToCornerstone />} />

                          {/* Philanthropy routes */}
                          <Route path="/philanthropy" element={<PhilanthropyPage />} />
                          <Route path="/philanthropy/endowment" element={<EndowmentFundPage />} />
                          <Route path="/philanthropy/license-plates" element={<LicensePlatesPage />} />
                          <Route path="/philanthropy/builders-walk" element={<BuildersWalkPage />} />
                          <Route path="/philanthropy/scholarship" element={<ScholarshipPage />} />
                          <Route path="/philanthropy/scottish-rite" element={<ScottishRitePage />} />
                          <Route path="/philanthropy/royal-arch" element={<RoyalArchPage />} />
                          <Route path="/philanthropy/cryptic-masons" element={<CrypticMasonsPage />} />
                          <Route path="/philanthropy/knights-templar" element={<KnightsTemplarPage />} />
                          <Route path="/philanthropy/shriners" element={<ShrinersPage />} />
                          <Route path="/philanthropy/mmri" element={<MMRIPage />} />

                          {/* Authentication routes */}
                          <Route path="/login" element={<Login />} />
                          <Route path="/register" element={<Register />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/reset-password/:token" element={<ResetPassword />} />
                          
                          {/* 404 route */}
                          <Route path="/404" element={<NotFound />} />
                          <Route path="*" element={<Navigate to="/404" replace />} />
                        </Route>
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                  
                  {/* AI Assistant - only show if enabled */}
                  {assistantEnabled && <AIAssistant />}
                </div>
                <Notifications />
              </AuthProvider>
            </BrowserRouter>
          </AlienContext.Provider>
        </ThemeContext.Provider>
      </JotaiProvider>
    </QueryClientProvider>
  );
};

export default App;